// /gift/:token - no-login landing page for the Club VIP gift emails
// (docs/club-vip-gift/SPEC.md). The token in the URL is the only credential;
// puttr-api src/giftAddress.js owns the row. States: confirm (address on
// file) / form (none, or Edit) / done / already confirmed / invalid or expired.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ReactComponent as Logo } from '../../images/logo_header.svg';
import { API_URL } from '../../constants/Strings';
import AddressForm from './AddressForm';
import { addressLines } from './addressData';
import PuttrSpinner from '../common/PuttrSpinner';
import '../games/GameView.css';
import './GiftAddress.css';

const EXPIRED_COPY = 'This link has expired. Sign in to app.puttr.co and go to Profile → Mailing address.';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function AddressCard({ address, className = '' }) {
  const lines = addressLines(address);
  return (
    <div className={'gcard addrCard ' + className}>
      {lines.map((l, i) => <div key={i} className={i === 0 ? 'addrLineName' : 'addrLine'}>{l}</div>)}
      {address && address.phone ? <div className="addrPhone">{address.phone}</div> : null}
    </div>
  );
}

export default function GiftAddress() {
  const { token } = useParams();
  const [data, setData] = useState(null);      // { firstName, lastName, status, country, address, confirmedAt }
  const [view, setView] = useState('loading'); // loading | confirm | form | done | confirmed | invalid | expired | error
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    document.body.classList.add('dark');
    document.title = 'Puttr · Mailing address';
    return () => document.body.classList.remove('dark');
  }, []);

  useEffect(() => {
    let alive = true;
    axios.get(API_URL + '/gift/' + encodeURIComponent(token))
      .then(res => {
        if (!alive) return;
        const d = res.data;
        setData(d);
        if (d.status === 'confirmed') setView('confirmed');
        else if (d.address) setView('confirm');
        else setView('form');
      })
      .catch(err => {
        if (!alive) return;
        const code = err.response && err.response.status;
        setView(code === 404 ? 'invalid' : code === 410 ? 'expired' : 'error');
      });
    return () => { alive = false; };
  }, [token]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(API_URL + '/gift/' + encodeURIComponent(token) + '/confirm');
      setData(res.data); setJustSaved(true); setView('done');
    } catch (err) {
      const code = err.response && err.response.status;
      setView(code === 410 ? 'expired' : code === 404 ? 'invalid' : 'error');
    }
    setBusy(false);
  };

  const save = async (address) => {
    const res = await axios.post(API_URL + '/gift/' + encodeURIComponent(token), address);
    setData(res.data); setJustSaved(true); setView('done');
  };

  const body = () => {
    switch (view) {
      case 'loading':
        return <div className="giftCenter"><PuttrSpinner /></div>;

      case 'confirm':
        return (
          <>
            <h1 className="giftTitle">Confirm your mailing address</h1>
            <AddressCard address={data.address} />
            <div className="giftActions">
              <button className="btnGreen" onClick={confirm} disabled={busy}>{busy ? 'Confirming…' : 'Confirm'}</button>
              <button className="giftLink" onClick={() => setView('form')} disabled={busy}>Edit address</button>
            </div>
          </>
        );

      case 'form':
        return (
          <>
            <h1 className="giftTitle">{data && data.address ? 'Edit your mailing address' : 'Where should we send it?'}</h1>
            <AddressForm
              dark
              initial={data && data.address}
              defaults={{ shipName: data ? [data.firstName, data.lastName].filter(Boolean).join(' ') : '', country: (data && data.country) || 'US' }}
              onSave={save}
              onCancel={data && data.address ? () => setView(data.status === 'confirmed' ? 'confirmed' : 'confirm') : null}
              submitLabel="Save address"
            />
          </>
        );

      case 'done':
        return (
          <>
            <div className="giftCheck" aria-hidden="true">✓</div>
            <h1 className="giftTitle">Done. Your gift is on its way soon.</h1>
            <p className="giftLead">We'll send it to:</p>
            <AddressCard address={data.address} />
            <div className="giftActions">
              <button className="giftLink" onClick={() => setView('form')}>Edit address</button>
            </div>
          </>
        );

      case 'confirmed':
        return (
          <>
            <h1 className="giftTitle">Your mailing address is confirmed</h1>
            <p className="giftLead">{justSaved ? 'Saved.' : `Confirmed on ${fmtDate(data.confirmedAt)}.`} Nothing else to do.</p>
            <AddressCard address={data.address} />
            <div className="giftActions">
              <button className="giftLink" onClick={() => setView('form')}>Edit address</button>
            </div>
          </>
        );

      case 'invalid':
      case 'expired':
        return (
          <>
            <h1 className="giftTitle">{view === 'expired' ? 'This link has expired' : "This link isn't valid"}</h1>
            <p className="giftLead">{EXPIRED_COPY}</p>
            <div className="giftActions">
              <a className="btnGhost" href="/profile">Sign in to app.puttr.co</a>
            </div>
          </>
        );

      default:
        return (
          <>
            <h1 className="giftTitle">Something went wrong</h1>
            <p className="giftLead">We couldn't load your details. Give it a moment and open the link from your email again.</p>
          </>
        );
    }
  };

  return (
    <div className="darkPage giftPage">
      <div className="topBar">
        <a href="https://puttr.co" aria-label="Puttr"><Logo className="logo" /></a>
      </div>
      <div className="page giftBody">
        {body()}
        <div className="footNote">Puttr Club · Your address is used only to send this gift.</div>
      </div>
    </div>
  );
}
